import { IconButton, TextButton } from '@creditornot/cb-button';
import { InputContainer } from '@creditornot/cb-form';
import { Cross, Plus } from '@creditornot/cb-icons';
import { cssThemedValue } from '@creditornot/cb-ingredients';
import { Input, Textarea } from '@creditornot/cb-input';
import { SegmentedControl } from '@creditornot/cb-segmented-control';
import { Select } from '@creditornot/cb-select';
import React, { useMemo, useState } from 'react';
import { Field, useFormState } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { NutritionSetting } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { RFFFieldErrorWrapper } from 'common/components';
import { localize } from 'common/utils';
import { PRODUCT_INFO_FIELD_MAX_LENGTH } from 'modules/menu/constants';
import { useOfferingMenuPermissionsContext } from 'modules/shared-menu/contexts/OfferingMenuPermissionsContext';

import { BaseField } from './types';
import { FormRowRenderer } from '../FormRowRenderer';
import { ProductInfoValue } from '../OfferDetailsFormFields.styles';
import { FormValues } from '../types';

type NutritionValue = {
  id: string;
  value: string | undefined;
};

type NutritionFieldsProps = BaseField & {
  nutritionSettings: NutritionSetting[];
  activeFieldType: 'text' | 'table';
  onChangeFieldType: (type: 'text' | 'table') => void;
};

const NutritionValuesContainer = styled.div`
  max-width: 392px;
  width: 100%;
`;

const NutritionValuesRow = styled.div`
  display: flex;
`;

const NutritionValueLabel = styled.div`
  vertical-align: top;
  width: 200px;
  padding-inline-end: 32px;
`;

const NutritionValuesFieldContainer = styled.div`
  flex: 1;
  margin-block-end: 8px;
`;

const StyledIconButton = styled(IconButton)`
  margin-inline-start: 4px;
`;

const NegativeSpace = styled.div`
  display: block;
  width: 40px;
`;

const SegmentedControlContainer = styled.div`
  margin-block-end: 24px;
`;

const BaselineSelect = styled(Select)`
  margin-block-end: 24px;
`;

const StyledInputContainer = styled(InputContainer)`
  max-width: 392px;
`;

const StyledTextButton = styled(TextButton)`
  margin-block-start: 16px;
`;

const NutrientSelect = styled(Select)`
  margin-block-end: 8px;
`;

const NutritionBaseline = styled.div`
  border-block-end: 1px solid ${cssThemedValue('textPrimary')};
  padding-block-end: 12px;
`;

const NutritionValueRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  & + & {
    border-block-start: 1px solid ${cssThemedValue('borderPrimary')};
  }
`;

const AdvancedDetailsTextarea = styled(Textarea).attrs({
  autoresizeMaxRows: 10,
  maxLength: PRODUCT_INFO_FIELD_MAX_LENGTH,
})``;

const nutritionBaselineTranslationKeys = {
  per_100g: 'menu-item.nutrition-baseline-100g',
  per_100ml: 'menu-item.nutrition-baseline-100ml',
};

export const NutritionFacts = ({
  activeFieldType,
  nutritionSettings,
  onChangeFieldType,
  sidePanelMode,
  offer,
  menuLocalizer,
}: NutritionFieldsProps) => {
  const menuPermissions = useOfferingMenuPermissionsContext();
  const intl = useIntl();

  const [visibleNutrientSelectors, setVisibleNutrientSelectors] = useState(0);
  const { values } = useFormState<FormValues>();
  const nutritionDataIndexedById = useMemo(
    () =>
      nutritionSettings.reduce<Record<string, { label: string; unit: string; default: boolean }>>((acc, curr) => {
        acc[curr.id] = {
          label: localize(curr.name, intl.locale),
          unit: localize(curr.unit, intl.locale),
          default: curr.ask_by_default,
        };
        return acc;
      }, {}),
    [intl.locale, nutritionSettings],
  );

  const nutritionValuesDropdownOptions = useMemo(() => {
    const addedNutrientIds = values.nutritionValues.map(value => value.id);
    return nutritionSettings
      .filter(value => !addedNutrientIds.includes(value.id))
      .sort((a, b) => a.table_row_number - b.table_row_number)
      .map(value => ({
        value: value.id,
        label: `${localize(value.name, intl.locale)} (${localize(value.unit, intl.locale)})`,
      }));
  }, [values.nutritionValues, nutritionSettings, intl.locale]);

  const formattedNutritionValues = useMemo(() => {
    if (!offer?.nutrition_values || !nutritionSettings.length) {
      return [];
    }

    return offer.nutrition_values
      .map(value => {
        const nutritionSetting = nutritionSettings.find(opt => opt.id === value.nutrition_value_id);
        return {
          label: localize(nutritionSetting?.name || [], intl.locale),
          amount: `${value.amount} ${localize(nutritionSetting?.unit || [], intl.locale)}`,
          position: nutritionSetting?.table_row_number || 9999,
        };
      })
      .sort((a, b) => a.position - b.position);
  }, [intl.locale, nutritionSettings, offer?.nutrition_values]);

  const previewComponent =
    !offer?.nutrition_facts && offer?.nutrition_values && offer?.nutrition_values.length > 0 ? (
      <NutritionValuesContainer>
        <NutritionBaseline>
          {offer.nutrition_values_baseline
            ? intl.formatMessage({ id: nutritionBaselineTranslationKeys[offer?.nutrition_values_baseline] })
            : '-'}
        </NutritionBaseline>
        <ProductInfoValue>
          {formattedNutritionValues.map(value => {
            return (
              <NutritionValueRow key={`${value.label}-${value.amount}`}>
                <div>{value.label}</div>
                <div>{value.amount}</div>
              </NutritionValueRow>
            );
          })}
        </ProductInfoValue>
      </NutritionValuesContainer>
    ) : (
      <ProductInfoValue>{menuLocalizer(offer?.nutrition_facts) || '-'}</ProductInfoValue>
    );

  const editComponent = (
    <>
      <SegmentedControlContainer>
        <StyledInputContainer>
          <SegmentedControl<'text' | 'table'>
            options={[
              { label: 'Text', value: 'text' },
              { label: 'Table', value: 'table' },
            ]}
            onChange={onChangeFieldType}
            value={activeFieldType}
          />
        </StyledInputContainer>
      </SegmentedControlContainer>
      {activeFieldType === 'text' && (
        <Field<string> name="nutritionFacts">
          {field => (
            <StyledInputContainer
              characterCount={{
                value: field.input.value?.length || 0,
                max: PRODUCT_INFO_FIELD_MAX_LENGTH,
              }}
            >
              <AdvancedDetailsTextarea
                {...field.input}
                placeholder={intl.formatMessage({ id: 'menu.placeholder-unset' })}
                spellCheck
              />
            </StyledInputContainer>
          )}
        </Field>
      )}
      {activeFieldType === 'table' && (
        <>
          <Field<string> name="nutritionValuesBaseline">
            {field => (
              <RFFFieldErrorWrapper field={field}>
                {input => (
                  <StyledInputContainer>
                    <BaselineSelect
                      {...input}
                      options={[
                        { value: 'per_100g', label: intl.formatMessage({ id: 'menu-item.nutrition-baseline-100g' }) },
                        {
                          value: 'per_100ml',
                          label: intl.formatMessage({ id: 'menu-item.nutrition-baseline-100ml' }),
                        },
                      ]}
                    />
                  </StyledInputContainer>
                )}
              </RFFFieldErrorWrapper>
            )}
          </Field>

          <NutritionValuesContainer>
            <FieldArray<NutritionValue> name="nutritionValues">
              {({ fields }) => (
                <div>
                  {fields.map((name, index) => {
                    const fieldId = fields.value[index].id;
                    return (
                      <NutritionValuesRow key={name}>
                        <NutritionValueLabel>{nutritionDataIndexedById[fieldId].label}</NutritionValueLabel>
                        <NutritionValuesFieldContainer>
                          <Field<string> name={`${name}.value`}>
                            {field => (
                              <RFFFieldErrorWrapper field={field}>
                                {input => (
                                  <Input {...input} suffixText={nutritionDataIndexedById[fieldId].unit} stretch />
                                )}
                              </RFFFieldErrorWrapper>
                            )}
                          </Field>
                        </NutritionValuesFieldContainer>

                        {nutritionDataIndexedById[fieldId].default ? (
                          <NegativeSpace />
                        ) : (
                          <StyledIconButton
                            size="small"
                            variant="grey"
                            background="animated"
                            onClick={() => fields.remove(index)}
                          >
                            <Cross />
                          </StyledIconButton>
                        )}
                      </NutritionValuesRow>
                    );
                  })}
                  {visibleNutrientSelectors > 0 &&
                    Array.from({ length: visibleNutrientSelectors }).map((_, index) => (
                      <StyledInputContainer key={`select-nutrient-${index}`}>
                        <NutrientSelect
                          options={nutritionValuesDropdownOptions}
                          onChange={id => {
                            if (id !== null) {
                              fields.push({ id, value: undefined });
                              setVisibleNutrientSelectors(curr => curr - 1);
                            }
                          }}
                        />
                      </StyledInputContainer>
                    ))}

                  <StyledTextButton
                    variant="blue"
                    icon={<Plus />}
                    onClick={() => setVisibleNutrientSelectors(curr => curr + 1)}
                    disabled={visibleNutrientSelectors === nutritionValuesDropdownOptions.length}
                  >
                    <FormattedMessage id="menu-item.add-nutrient" />
                  </StyledTextButton>
                </div>
              )}
            </FieldArray>
          </NutritionValuesContainer>
        </>
      )}
    </>
  );

  return (
    <FormRowRenderer
      hasContentEditPermission={menuPermissions.contentEdit}
      shouldDisplayCreateTag={!values.nutritionFacts && !values.nutritionValuesBaseline}
      label={intl.formatMessage({ id: 'menu.nutrition-facts' })}
      previewComponent={previewComponent}
      editComponent={editComponent}
      sidePanelMode={sidePanelMode}
      offer={offer}
    />
  );
};
